import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { parseHiBidCSV } from '../utils/hibidParser';
import { Auction, AuctionLot } from '../types/auction.types';
import toast from 'react-hot-toast';

const UpdateAuctionCSV: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedLots, setParsedLots] = useState<AuctionLot[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);

  const loadAuction = async () => {
    if (!id) return;

    try {
      const auctionDoc = await getDoc(doc(db, 'auctions', id));
      if (!auctionDoc.exists()) {
        toast.error('Auction not found');
        navigate('/auctions');
        return;
      }

      const data = auctionDoc.data();
      setAuction({
        id: auctionDoc.id,
        ...data,
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lots: data.lots || [],
      } as Auction);
    } catch (error) {
      console.error('Error loading auction:', error);
      toast.error('Failed to load auction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setParsing(true);
    setParsedLots([]);
    setParseErrors([]);
    
    try {
      const text = await selectedFile.text();
      const result = parseHiBidCSV(text);
      
      setParsedLots(result.lots);
      setParseErrors(result.errors);
      
      if (result.lots.length > 0) {
        toast.success(`Parsed ${result.lots.length} lots successfully!`);
      }
      
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} errors found`);
      }
      
    } catch (error) {
      toast.error('Failed to parse CSV file');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const handleUpdate = async () => {
  if (!id || !auction || parsedLots.length === 0) return;

  setUpdating(true);

  try {
    // Create a map of existing lots by SKU to preserve images
    const existingLotsMap = new Map(
      auction.lots.map(lot => [lot.sku, lot])
    );

    // Helper function to remove undefined values
    const cleanLot = (lot: any) => {
      return Object.fromEntries(
        Object.entries(lot).filter(([_, v]) => v !== undefined)
      );
    };

    // Update lots: preserve images from existing lots, update everything else
    const updatedLots = parsedLots.map(newLot => {
      const existingLot = existingLotsMap.get(newLot.sku);
      
      const mergedLot = {
        ...newLot,
        images: existingLot?.images || [], // Preserve existing images
        currentBid: existingLot?.currentBid || newLot.startingBid,
        bidCount: existingLot?.bidCount || 0,
        status: existingLot?.status || 'pending',
      };

      // Remove undefined values
      return cleanLot(mergedLot);
    });

    // Update Firestore
    await updateDoc(doc(db, 'auctions', id), {
      lots: updatedLots,
      totalLots: updatedLots.length,
      updatedAt: new Date(),
    });

      toast.success(`Auction updated with ${updatedLots.length} lots!`);
      navigate(`/auctions/${id}`);

    } catch (error) {
      console.error('Error updating auction:', error);
      toast.error('Failed to update auction');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading auction...</p>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Auction not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <button
          onClick={() => navigate(`/auctions/${id}`)}
          className="text-primary-600 hover:text-primary-700 mb-2 flex items-center gap-2"
        >
          ← Back to Auction
        </button>
        <h1 className="text-3xl font-bold">Re-import CSV</h1>
        <p className="text-gray-600 mt-1">
          Update "{auction.title}" with revised HiBid export
        </p>
      </div>

      {/* Current Status */}
      <div className="card bg-blue-50 border-blue-200">
        <h2 className="text-xl font-bold mb-4">Current Auction</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Current Lots</p>
            <p className="text-2xl font-bold text-blue-600">{auction.totalLots}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Lots with Images</p>
            <p className="text-2xl font-bold text-blue-600">
              {auction.lots.filter(l => l.images?.length > 0).length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <p className="text-lg font-semibold text-blue-800">{auction.status}</p>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Upload Revised CSV</h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
            disabled={parsing || updating}
          />
          <label htmlFor="csv-upload" className="cursor-pointer">
            {file ? (
              <div>
                <p className="text-lg font-semibold text-primary-600">{file.name}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {parsedLots.length} lots parsed
                </p>
                <button className="btn-secondary mt-4" disabled={parsing || updating}>
                  Choose Different File
                </button>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-4">📤</div>
                <p className="text-lg font-semibold">Click to upload revised CSV</p>
                <p className="text-sm text-gray-600 mt-2">
                  Lots will be matched by SKU - images will be preserved
                </p>
              </div>
            )}
          </label>
        </div>
        
        {parsing && (
          <p className="text-center text-gray-600 mt-4">Parsing CSV...</p>
        )}
        
        {parseErrors.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-semibold text-red-800 mb-2">
              {parseErrors.length} errors found:
            </p>
            <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
              {parseErrors.map((error, i) => (
                <li key={i}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Preview Changes */}
      {parsedLots.length > 0 && (
        <>
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Preview Changes</h2>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">New Lots</p>
                <p className="text-2xl font-bold text-green-600">{parsedLots.length}</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Images Preserved</p>
                <p className="text-2xl font-bold text-blue-600">
                  {parsedLots.filter(lot => {
                    const existing = auction.lots.find(l => l.sku === lot.sku);
                    return existing?.images && existing.images.length > 0;
                  }).length}
                </p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600">Lot Count Change</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {parsedLots.length > auction.totalLots ? '+' : ''}
                  {parsedLots.length - auction.totalLots}
                </p>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {parsedLots.slice(0, 10).map((lot, i) => {
                const existingLot = auction.lots.find(l => l.sku === lot.sku);
                const hasImages = existingLot?.images && existingLot.images.length > 0;
                
                return (
                  <div key={i} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-600">
                          LOT {lot.lotNumber} (SKU: {lot.sku})
                        </p>
                        <p className="font-bold mt-1">{lot.title}</p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {lot.description}
                        </p>
                        {hasImages && (
                          <p className="text-xs text-green-600 mt-2">
                            ✓ {existingLot?.images?.length || 0} images will be preserved
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm text-gray-600">Starting Bid</p>
                        <p className="font-bold text-primary-600">
                          ${(lot.startingBid / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {parsedLots.length > 10 && (
                <p className="text-center text-gray-600 text-sm">
                  ... and {parsedLots.length - 10} more lots
                </p>
              )}
            </div>
          </div>

          {/* Update Button */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate(`/auctions/${id}`)}
              className="btn-secondary"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              className="btn-primary"
              disabled={updating}
            >
              {updating ? 'Updating Auction...' : `Update with ${parsedLots.length} Lots`}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default UpdateAuctionCSV;